import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Download, FileText, CheckCircle, AlertCircle, Banknote, Share2, PenLine, FileDown, Receipt, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { apiUpload, apiPatch, apiPost, apiGet, apiGetText } from '../../../api/client';
import { useBlobDownload } from '../../../hooks/useBlobDownload';
import type { Event } from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { LinkShareBlock } from '../../../components/ui/LinkShareBlock';
import { formatDate, formatCurrency, formatPaymentMethod } from '../../../utils/format';
import { copyToClipboard } from '../../../utils/clipboard';

export default function ContractTab({ event }: { event: Event }) {
  const qc = useQueryClient();
  const fileRef = React.useRef<HTMLInputElement>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['event', event.id] });
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // Fetch preview HTML
  const { data: previewResult } = useQuery<string, Error>({
    queryKey: ['contract-preview', event.id],
    queryFn: () => apiGetText(`/events/${event.id}/contract-preview`),
    enabled: showPreview,
    staleTime: 0, // always fresh
  });

  useEffect(() => {
    if (previewResult) setPreviewHtml(previewResult);
  }, [previewResult]);

  const [showSignForm, setShowSignForm] = useState(false);
  const [signerName, setSignerName] = useState('');

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiUpload(`/events/${event.id}/contract`, fd);
    },
    onSuccess: () => { invalidate(); toast.success('Contratto caricato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const signMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (signerName.trim()) body.signerName = signerName.trim();
      await apiPatch(`/events/${event.id}/contract/sign`, body);
    },
    onSuccess: () => {
      invalidate();
      setShowSignForm(false);
      toast.success('Contratto marcato come firmato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) uploadMutation.mutate(file);
  };

  const depositPending = false;

  // Quote sharing
  const [quoteLink, setQuoteLink] = useState<string | null>(
    event.quoteToken ? `${window.location.origin}/api/public/quote/${event.quoteToken}` : null
  );

  const quoteTokenMutation = useMutation({
    mutationFn: () => apiPost<{ quoteToken: string; quoteUrl: string }>(`/events/${event.id}/quote-token`),
    onSuccess: (data) => {
      const url = `${window.location.origin}/api/public/quote/${data.quoteToken}`;
      setQuoteLink(url);
      invalidate();
      toast.success('Link preventivo generato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { isDownloading: isDownloadingContract, downloadBlob } = useBlobDownload();

  // Contract PDF download
  const downloadContractPdf = () => {
    downloadBlob(`/events/${event.id}/contract-pdf`, `contratto-${event.id}.pdf`, 'Contratto PDF');
  };

  // Reset contract snapshot to regenerate from current template
  const resetSnapshotMutation = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/contract/reset-snapshot`),
    onSuccess: () => {
      toast.success('Snapshot resettato. Il prossimo PDF userà il template corrente.');
      invalidate();
    },
    onError: () => toast.error('Errore durante il reset dello snapshot'),
  });

  const hasSnapshot = !!event.contractTemplateSnapshot;

  // Digital sign link
  const [signLink, setSignLink] = useState<string | null>(
    event.contractSignToken ? `${window.location.origin}/api/public/contract-sign/${event.contractSignToken}` : null
  );

  const signTokenMutation = useMutation({
    mutationFn: () => apiPost<{ contractSignToken: string; signUrl: string }>(`/events/${event.id}/contract-sign-token`),
    onSuccess: (data) => {
      const url = `${window.location.origin}/api/public/contract-sign/${data.contractSignToken}`;
      setSignLink(url);
      invalidate();
      copyToClipboard(url).then(() =>
        toast.success('Link firma generato e copiato')
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Invoice
  const [invoiceLink, setInvoiceLink] = useState<string | null>(
    event.invoiceToken ? `${window.location.origin}/api/public/invoice/${event.invoiceToken}` : null
  );
  const [invoiceLabel, setInvoiceLabel] = useState<string | null>(
    event.invoiceNumber != null ? `${new Date().getFullYear()}/${String(event.invoiceNumber).padStart(3, '0')}` : null
  );

  const invoiceTokenMutation = useMutation({
    mutationFn: () => apiPost<{ invoiceToken: string; invoiceNumber: number; year: number; invoiceUrl: string }>(
      `/events/${event.id}/invoice-token`
    ),
    onSuccess: (data) => {
      const url = `${window.location.origin}/api/public/invoice/${data.invoiceToken}`;
      setInvoiceLink(url);
      setInvoiceLabel(`${data.year}/${String(data.invoiceNumber).padStart(3, '0')}`);
      invalidate();
      toast.success(`Fattura N. ${data.year}/${String(data.invoiceNumber).padStart(3, '0')} generata`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Deposit pending reminder */}
      {depositPending && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
          background: 'var(--color-warning-highlight, #fffbeb)',
          border: '1px solid var(--color-warning)',
          borderRadius: 'var(--radius-card)',
          padding: '0.75rem 1rem',
        }}>
          <AlertCircle size={15} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: '0.1rem' }} />
          <p style={{ fontSize: 'var(--text-sm)', lineHeight: 1.5 }}>
            <strong>Acconto in attesa:</strong> contratto firmato ma acconto di{' '}
            <strong>{formatCurrency(event.depositAmount)}</strong> non ancora registrato.{' '}
            Registralo dalla sezione <strong>Pagamenti</strong> nel tab Overview.
          </p>
        </div>
      )}

      {/* Upload area */}
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={handleFileDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${event.contractUrl ? 'rgba(255,255,255,0.10)' : 'var(--color-primary)'}`,
          borderRadius: 'var(--radius-card)',
          padding: '2.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
          cursor: 'pointer',
          background: event.contractUrl ? 'var(--glass-bg-card)' : 'var(--color-primary-highlight)',
          transition: 'all var(--transition-interactive)',
        }}
      >
        <Upload size={28} style={{ color: 'var(--color-primary)' }} />
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontWeight: 600, color: 'var(--color-text)' }}>
            {event.contractUrl ? 'Sostituisci contratto' : 'Carica contratto'}
          </p>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
            Trascina un PDF qui o clicca per scegliere
          </p>
        </div>
        {uploadMutation.isPending && (
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary)' }}>Caricamento in corso…</div>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) uploadMutation.mutate(e.target.files[0]); }} />
      </div>

      {/* Contract info + sign */}
      {event.contractUrl && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          padding: '1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <FileText size={20} style={{ color: 'var(--color-primary)' }} />
              <div>
                <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                  {event.contractUrl.split('/').pop()}
                </p>
                {event.contractSignedAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.25rem' }}>
                    <CheckCircle size={12} style={{ color: 'var(--color-success)' }} />
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}>
                      Firmato il {formatDate(event.contractSignedAt)}
                    </span>
                  </div>
                )}
                {event.depositPaidAt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.125rem' }}>
                    <Banknote size={12} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)' }}>
                      Acconto {formatCurrency(event.depositAmount)} ricevuto il {formatDate(event.depositPaidAt)}
                      {event.depositPaidMethod ? ` · ${formatPaymentMethod(event.depositPaidMethod)}` : ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <a
              href={event.contractUrl}
              download
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.375rem 0.875rem',
                background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text)',
                textDecoration: 'none',
              }}
            >
              <Download size={14} /> Scarica
            </a>
          </div>

          {/* Sign + deposit section (only if not yet signed) */}
          {!event.contractSignedAt && (
            <>
              {!showSignForm ? (
                <Button
                  variant="secondary"
                  onClick={() => setShowSignForm(true)}
                  icon={<CheckCircle size={14} />}
                >
                  Firma contratto
                </Button>
              ) : (
                <div style={{
                  background: 'var(--glass-bg-card)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 'var(--radius-card)',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.875rem',
                }}>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    Firma contratto
                  </p>

                  <div>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.375rem', fontWeight: 500 }}>
                      Nome firmatario — <span style={{ fontWeight: 400 }}>opzionale</span>
                    </p>
                    <Input
                      placeholder="Nome e cognome del cliente"
                      value={signerName}
                      onChange={e => setSignerName(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                    <CheckCircle size={14} style={{ color: 'var(--color-success)', flexShrink: 0, marginTop: '0.1rem' }} />
                    <span>Contratto firmato — <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>obbligatorio</span></span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button size="sm" variant="ghost" onClick={() => setShowSignForm(false)}>Annulla</Button>
                    <Button size="sm" onClick={() => signMutation.mutate()} loading={signMutation.isPending} icon={<CheckCircle size={13} />}>
                      Firma contratto
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!event.contractUrl && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)',
          padding: '1.25rem',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--text-sm)',
          textAlign: 'center',
        }}>
          Nessun contratto caricato
        </div>
      )}

      {/* Contratto PDF da template */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.875rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FileDown size={15} style={{ color: 'var(--color-primary)' }} />
          <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Contratto PDF</p>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          Genera il PDF del contratto compilato con i dati dell'evento e del cliente, usando il template personalizzabile nelle Impostazioni.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            icon={<Eye size={14} />}
            onClick={() => setShowPreview(true)}
          >
            Anteprima
          </Button>
          <Button
            variant="secondary"
            icon={<FileDown size={14} />}
            onClick={downloadContractPdf}
            loading={isDownloadingContract}
          >
            Scarica PDF contratto
          </Button>
          {hasSnapshot && (
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw size={14} />}
              onClick={() => {
                if (confirm('Rigenerare il contratto dal template corrente? Le modifiche successive al template verranno applicate.')) {
                  resetSnapshotMutation.mutate();
                }
              }}
              loading={resetSnapshotMutation.isPending}
              title="Rigenera dal template corrente"
            >
              Rigenera
            </Button>
          )}
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowPreview(false)}
        >
          <div
            style={{
              width: '90vw', maxWidth: '900px', height: '85vh',
              background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'var(--glass-bg-card)',
            }}>
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>Anteprima contratto</h3>
              <button onClick={() => setShowPreview(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem',
                color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)',
              }}>✕</button>
            </div>
            <iframe
              title="contract-preview"
              style={{ flex: 1, border: 'none' }}
              srcDoc={previewHtml || '<p style="padding:2rem;color:#999">Caricamento...</p>'}
            />
          </div>
        </div>
      )}

      {/* Contract preview state management */}

      {/* Firma digitale */}
      {event.contractSignedAt || event.contractSignerName ? (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: '1px solid var(--color-success)',
          borderRadius: 'var(--radius-card)',
          padding: '1.25rem',
          display: 'flex', flexDirection: 'column', gap: '0.875rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PenLine size={15} style={{ color: 'var(--color-success)' }} />
              <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Firma digitale</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <CheckCircle size={13} style={{ color: 'var(--color-success)' }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)', fontWeight: 600 }}>Firmato</span>
            </div>
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span><strong>Firmatario:</strong> {event.contractSignerName || '—'}</span>
            {event.contractSignedAt && <span><strong>Data:</strong> {formatDate(event.contractSignedAt)}</span>}
          </div>

          {event.contractSignaturePath && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>
                Firma autografa
              </p>
              <img
                src={event.contractSignaturePath}
                alt="Firma cliente"
                style={{ maxWidth: '200px', maxHeight: '80px', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', background: 'var(--glass-bg-card)' }}
              />
            </div>
          )}
        </div>
      ) : (
        <LinkShareBlock
          icon={<PenLine size={15} style={{ color: 'var(--color-primary)' }} />}
          title="Firma digitale"
          description="Invia al cliente un link univoco: aprirà il contratto e potrà firmarlo digitalmente (eIDAS). La firma viene registrata con nome, data e IP."
          link={signLink}
          onGenerate={() => signTokenMutation.mutate()}
          generating={signTokenMutation.isPending}
          generateLabel="Genera link firma digitale"
          openLabel="Anteprima pagina firma"
        />
      )}

      {/* Preventivo web condivisibile */}
      <LinkShareBlock
        icon={<Share2 size={15} style={{ color: 'var(--color-primary)' }} />}
        title="Preventivo condivisibile"
        description="Genera un link pubblico con il preventivo formattato, pronto per la stampa o da inviare al cliente."
        link={quoteLink}
        onGenerate={() => quoteTokenMutation.mutate()}
        generating={quoteTokenMutation.isPending}
        generateLabel="Genera link preventivo"
        openLabel="Apri preventivo"
      />

      {/* Fattura */}
      <LinkShareBlock
        icon={<Receipt size={15} style={{ color: 'var(--color-primary)' }} />}
        title="Fattura"
        description={`Genera una fattura HTML pronta per la stampa con dati fiscali, regime applicabile e IBAN.${!invoiceLabel ? ' Il numero verrà assegnato definitivamente al momento della generazione.' : ''}`}
        link={invoiceLink}
        onGenerate={() => invoiceTokenMutation.mutate()}
        generating={invoiceTokenMutation.isPending}
        generateLabel="Genera fattura"
        badgeLabel={invoiceLabel ? `N. ${invoiceLabel}` : undefined}
        warningMessage={invoiceLabel ? `Il numero fattura N. ${invoiceLabel} è definitivo e non può essere rigenerato.` : undefined}
        showOpenPdf
      />
    </div>
  );
}
